import { useEffect, useMemo, useState } from 'react';
import EventIcon from '@mui/icons-material/Event';
import { InputAdornment, TextField } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { DatePicker } from '@mui/x-date-pickers';

const DateEditor = ({
  disabled,
  enableEdit,
  onChange,
  onValidationChange,
  textInputProps,
  value,
  views
}) => {
  const [internalValue, setInternalValue] = useState();
  const [isValid, setIsValid] = useState(true);
  const [open, setOpen] = useState(false);

  const dateProps = useMemo(() => ({
    maxDate: new Date(Number(new Date("10000")) - 1),
    minDate: new Date("1753"),
  }), []);

  const initialValue = useMemo(() => (
    (value === null) ? null : new Date(value)
  ), [value]);

  const debounceChange = useMemo(
    () =>
      debounce((newValue) => {
        var isDirty = (newValue?.valueOf() !== initialValue?.valueOf());
        if (isDirty) onChange(newValue);
        onValidationChange?.(true);
      }, 800),
    [initialValue]
  );

  useEffect(() => {
    setInternalValue(initialValue);
  }, [initialValue]);

  const handleChangeValue = (newValue) => {
    setInternalValue(newValue);
    if (isValidDate(newValue)) {
      debounceChange(newValue, true);
      if (!isValid) {
        setIsValid(true);
      }
    }
    else if (isValid) {
      onValidationChange?.(false);
      setIsValid(false);
    }
  };

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  const handleShiftKeyDown = (e) => e.shiftKey && e.stopPropagation();
  const handleShiftKeyUp = (e) => (e.key === "Shift") && e.stopPropagation();

  const isValidDate = (date) => isValidDateObject(date) && (dateProps.minDate <= date) && (date <= dateProps.maxDate);
  const isValidDateObject = (o) => (o === null || o instanceof Date && !isNaN(o));

  return (
    <DatePicker
      components={{
        textField: TextField,
      }}
      componentsProps={{
        actionBar: {
          actions: ['clear']
        },
        popper: {
          onKeyDown: handleShiftKeyDown,
          onKeyUp: handleShiftKeyUp,
          zIndex: 1300,
        },
        textField: {
          InputProps: textInputProps,
          onClick: handleOpen,
          onKeyDown: handleShiftKeyDown,
          onKeyUp: handleShiftKeyUp,
          variant: 'standard',
        },
      }}
      {...dateProps}
      disabled={disabled}
      onChange={handleChangeValue}
      onClose={handleClose}
      open={enableEdit && !disabled && open}
      sx={{
        width: '100%'
      }}
      TransitionComponent={true}
      TransitionProps={{
        onExited: () => setOpen(false),
        unmountOnExit: true,
      }}
      value={internalValue}
      views={views}
    />
  );
};

export default DateEditor;

DateEditor.defaultProps = {
  disabled: false,
  enableEdit: false,
  textInputProps: {
    disableUnderline: true,
    endAdornment: //Set to null if not show.
      <InputAdornment position="end">
        <EventIcon sx={{ opacity: 0.7, fontSize: '16px' }} />
      </InputAdornment>,
    sx: {
      backgroundColor: '#ffffff',
      border: '1px solid rgba(0, 0, 0, 0)',
      borderRadius: "4px",
      color: 'rgba(0, 0, 0, 0.87)',
      cursor: "text",
      fontSize: '12px',
      minHeight: '1.5em',
      padding: '4px',
      textAlign: 'left',
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
      '&.Mui-focused': {
        border: '1px solid rgba(0, 0, 0, 0.5)',
      },
      '& .MuiInputBase-root': {
        padding: 0,
      },
    },
  },
  views: ['year', 'month', 'day'],
};
